import { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { useSelector, useDispatch } from 'BMapsRedux';
// Material UI
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
// Local
import { Loader } from 'BMapsSrc/Loader';
import { setPreferences } from '../../redux/prefs';
import { preferences, PREFS_TYPE } from '../../redux/prefs/constants';
import { addMenuItem } from '../../menu_cmds';
import { EventBroker } from '../../eventbroker';
import ModalPane from '../ModalPane';
import Select from '../common/Select';
import { MainButton } from '../common/Buttons';
import { useEventBrokerStateHandling } from '../common/helpers/useEventBrokerSubscription';

/**
 * Enum values this build offers: all of them, except for the 3D viewer, whose choices are the
 * backends the feature loader registered (Loader.CanvasVisualizers).
 */
function valueAvailable(pref) {
    if (pref.id !== 'Visualizer') return () => true;
    const backends = Loader.CanvasVisualizers || {};
    return (valObj) => !!backends[valObj.id];
}

/**
 * A 3D viewer this build has but that cannot run in this browser (isSupported()) is not
 * offered; the field says why instead. (Not a disabled MenuItem: Select wraps disabled items for
 * their tooltip, and MUI then makes the wrapper - which has no value - the clickable option.)
 * @returns {string|null} the sentence, or null when the value can be chosen
 */
function unavailableNote(pref, valObj) {
    if (pref.id !== 'Visualizer') return null;
    const Backend = (Loader.CanvasVisualizers || {})[valObj.id];
    if (!Backend || typeof Backend.isSupported !== 'function' || Backend.isSupported()) return null;
    return `${valObj.noun || valObj.label} is not available here (needs ${Backend.requirement || 'a newer browser'}).`;
}

function formValidate(values) {
    const errors = {};

    const { prefName, prefValue } = values;

    if (prefName === '') {
        errors.prefName = 'This field is required!';
    }

    if (prefValue === '') {
        errors.prefValue = 'This field is required!';
    }

    return errors;
}

export default function PreferencesPane({ ModalPaneProps }) {
    const prefs = useSelector((state) => state.prefs);
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        addMenuItem('PreferencesPane', 'Preferences', () => { EventBroker.publish('showPreferencesPane', true); });
    }, []);

    useEventBrokerStateHandling('showPreferencesPane', open, setOpen);

    function handleClose() {
        setOpen(false);
    }

    function handleSubmit(values) {
        dispatch(setPreferences(values));
    }

    // A pref (or an individual enum option) is shown when it carries no
    // `availability` tag, or its tag matches the current build's Loader flags.
    // Applied to whole prefs and to enum `values` so a single option (e.g. the
    // 'Local' minimization engine, loaded only in dev/qa) can be gated.
    const isAvailable = (item) => (
        !item.availability
            || (item.availability === 'lab' && Loader.AllowLabFeatures)
            || (item.availability === 'dev' && Loader.AllowDevFeatures)
    );

    const availablePrefs = preferences.filter(isAvailable);

    return (
        <ModalPane
            visible={open}
            title="Preferences"
            close={handleClose}
            closeOnEscape={handleClose}
            ContentProps={{
                sx: {
                    display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden',
                },
            }}
            {...ModalPaneProps}
        >
            <Formik
                initialValues={prefs}
                validate={formValidate}
                onSubmit={handleSubmit}
            >
                {({ values: formikValues }) => (
                    <Form
                        style={{
                            display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0,
                        }}
                    >
                        <div
                            style={{
                                flex: '1 1 auto', minHeight: 0, overflowY: 'auto', position: 'relative',
                            }}
                        >
                            {availablePrefs.map((pref) => {
                                const {
                                    id, label, type, values,
                                } = pref;

                                switch (type) {
                                    case PREFS_TYPE.boolean:
                                        return (
                                            <Grid container key={id} sx={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                                <InputLabel htmlFor={id}>{`${label}:`}</InputLabel>
                                                <Field
                                                    as={Switch}
                                                    name={id}
                                                    checked={formikValues[id]}
                                                />
                                            </Grid>
                                        );

                                    case PREFS_TYPE.enum: {
                                        const offered = values.filter(isAvailable)
                                            .filter(valueAvailable(pref));
                                        // nothing to choose (e.g. a build with one 3D viewer)
                                        if (offered.length < 2) return null;
                                        // why a value is not offered, then e.g. a trademark notice
                                        const notes = [
                                            ...offered.map((v) => unavailableNote(pref, v)),
                                            ...offered.map((v) => v.footnote),
                                        ].filter(Boolean);
                                        const choices = offered
                                            .filter((v) => !unavailableNote(pref, v));
                                        return (
                                            <Grid container key={id} style={{ marginBottom: '0.5rem' }}>
                                                <Field
                                                    as={Select}
                                                    name={id}
                                                    id={id}
                                                    label={label}
                                                    margin="dense"
                                                    helperText={notes.join(' ') || undefined}
                                                    slotProps={{ select: { native: false } }}
                                                >
                                                    {choices.map((valObj) => (
                                                        <MenuItem
                                                            key={valObj.id}
                                                            value={valObj.id}
                                                        >
                                                            {valObj.label}
                                                        </MenuItem>
                                                    ))}
                                                </Field>
                                            </Grid>
                                        );
                                    }

                                    case PREFS_TYPE.int:
                                        return (
                                            <Grid container key={id} style={{ marginBottom: '0.5rem' }}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    margin="dense"
                                                    variant="outlined"
                                                    type="number"
                                                    name={id}
                                                    label={label}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    default:
                                        console.error(`Invalid preference type: ${type}`);
                                        return null;
                                }
                            })}
                        </div>
                        <MainButton fullWidth type="submit" onClick={handleClose}>Save</MainButton>
                    </Form>
                )}
            </Formik>
        </ModalPane>
    );
}
